import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHome,
  faPlane,
  faHotel,
  faCar,
  faUsers,
  faUser,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons'
import RaceCard from "../components/RaceCard";

function Home() {
  const [races, setRaces] = useState([]);
  const [profile, setProfile] = useState(null);
  const [countdown, setCountdown] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/"); // kick out if not logged in
    }
  }, [navigate]);

  // load current profile (for avatar in quick actions)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => setProfile(data.user || null))   // <-- use data.user
      .catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/races', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => setRaces(Array.isArray(data) ? data : []))
      .catch(() => setRaces([]));
  }, []);

  // find next upcoming race
  const upcoming = races
    .map(r => ({ ...r, start_ts: r.start_date ? new Date(r.start_date).getTime() : 0 }))
    .filter(r => r.start_ts > Date.now())
    .sort((a, b) => {
      if (a.start_ts === b.start_ts) {
        return a.id - b.id; // Secondary sort by ID for stability
      }
      return a.start_ts - b.start_ts;
    });
  const nextRace = upcoming.length ? upcoming[0] : null;

  useEffect(() => {
    if (!nextRace) {
      setCountdown('—');
      return;
    }
    function tick() {
      const diff = nextRace.start_ts - Date.now();
      if (diff <= 0) {
        setCountdown('Started');
        return;
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setCountdown(`${d}d ${h}h ${m}m ${s}s`);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRace]);

  const heroImage = nextRace && nextRace.race_track_image ? `/assets/race-tracks/${nextRace.race_track_image}` : 'https://placehold.co/800x300?text=MotoGP';

  const quickActions = [
    { to: '/profile', label: 'Profile', icon: faUser },
    { to: '/hotels', label: 'Hotels', icon: faHotel },
    { to: '/flights', label: 'Flights', icon: faPlane },
    { to: '/car-rental', label: 'Car rentals', icon: faCar },
    { to: '/team', label: 'Team', icon: faUsers },
  ];

  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  if (isAdmin) quickActions.push({ to: '/admin', label: 'Admin', icon: faUserShield });

  const upcomingPreview = races
    .map(r => ({ ...r, start_ts: r.start_date ? new Date(r.start_date).getTime() : 0 }))
    .sort((a, b) => {
      if (a.start_ts === b.start_ts) {
        return a.id - b.id; // Secondary sort by ID for stability
      }
      return a.start_ts - b.start_ts;
    })
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-50">
      {/* Header with hero section and quick actions side-by-side */}
      <header className="mb-8 px-6 pt-6 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Hero banner - left side */}
          <div className="lg:col-span-2 bg-gradient-to-b from-black via-gray-950 to-black text-white rounded-xl p-8 shadow-2xl overflow-hidden relative border-l-4 border-yellow-400">
            <div className="absolute inset-0 opacity-5 bg-pattern"></div>
            <div className="relative z-10">
              {/* Race image */}
              <div className="mb-6">
                <img 
                  src={heroImage} 
                  alt={nextRace ? nextRace.name : 'MotoGP'} 
                  className="w-full h-48 object-cover rounded-lg shadow-lg border-2 border-yellow-400" 
                />
              </div>

              {/* Race info */}
              <div>
                <h1 className="text-4xl md:text-5xl font-black tracking-tight text-yellow-400">{nextRace ? nextRace.name : 'MotoGP DASHBOARD'}</h1>
                <p className="mt-2 text-lg font-semibold text-gray-300">
                  {nextRace 
                    ? `${new Date(nextRace.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} — ${new Date(nextRace.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`
                    : 'No upcoming races'
                  }
                </p>
                
                {/* Countdown and CTA */}
                <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="bg-gray-900/80 text-yellow-400 rounded-lg px-6 py-3 font-mono font-bold text-xl shadow-lg border-2 border-yellow-400">
                    {countdown}
                  </div>
                  {nextRace && (
                    <Link 
                      to={`/races/${nextRace.id}`} 
                      className="bg-yellow-400 text-black px-8 py-3 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl hover:bg-yellow-300 transition border-2 border-yellow-400"
                    >
                      View Full Race →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions - right side */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4">
          {quickActions.map(a => (
            <Link 
              key={a.to} 
              to={a.to} 
              className="bg-white border-2 border-gray-300 hover:border-yellow-400 hover:shadow-yellow-400/30 rounded-lg p-4 hover:shadow-lg transition transform hover:scale-105 flex flex-col items-center gap-3 shadow-md"
            >
              {a.to === '/profile' ? (
                <img
                  src={profile && profile.profile_pic ? `/assets/profile-pic/${profile.profile_pic}` : 'https://placehold.co/50x50?text=U'}
                  alt={profile && profile.name ? profile.name : 'Profile'}
                  className="w-12 h-12 rounded-lg object-cover border-2 border-gray-300"
                />
              ) : (
                <FontAwesomeIcon icon={a.icon} className="text-2xl text-gray-700" />
              )}
              <div className="text-sm font-bold text-gray-800 text-center">{a.label}</div>
            </Link>
          ))}
            </div>
          </div>
        </div>
      </header>

      {/* All races section */}
      <section className="px-6 pb-12 max-w-6xl mx-auto">
        <h2 className="text-3xl font-black text-gray-800 mb-6">All Races</h2>
        {races.length === 0 ? (
          <div className="p-8 bg-white border-2 border-gray-300 rounded-lg text-center text-gray-500 shadow-md">
            No races available
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {races.map(r => (
              <RaceCard key={r.id} race={r} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default Home;
